import { useMemo, useState, useCallback, useRef, useEffect, type CSSProperties } from 'react';
import { format } from 'date-fns';
import { Appointment } from '@/services/appointmentService';
import { cn } from '@/utils/cn';

interface DayTimelineViewProps {
  appointments: Appointment[];
  selectedAppointmentId: string | null;
  onSelectAppointment: (appointment: Appointment) => void;
  onTimeSlotClick?: (hour: number, minute: number) => void;
  /** Giornata visualizzata (data locale) — necessaria per calcolare i nuovi orari al drag */
  viewDate: Date;
  onAppointmentMoveEnd?: (appointment: Appointment, newStart: Date, newEnd: Date) => void | Promise<void>;
  startHour?: number;
  endHour?: number;
}

/** Altezza in px per un’ora di timeline (ogni slot da 15 min = ROW_HEIGHT/4). Raddoppiata per migliorare leggibilità e hit-area. */
const ROW_HEIGHT = 120;
const QUARTER_HEIGHT = ROW_HEIGHT / 4;
const MIN_DURATION_MS = 15 * 60 * 1000;
const DRAG_THRESHOLD_PX = 5;
const SNAP_MS = 15 * 60 * 1000;

function effectiveEndMs(startTime: string, endTime: string): number {
  const s = new Date(startTime).getTime();
  const e = new Date(endTime).getTime();
  return Math.max(e, s + MIN_DURATION_MS);
}

const statusColors: Record<string, { bg: string; border: string; text: string }> = {
  SCHEDULED: { bg: 'bg-blue-100', border: 'border-l-blue-500', text: 'text-blue-900' },
  CONFIRMED: { bg: 'bg-green-100', border: 'border-l-green-500', text: 'text-green-900' },
  IN_PROGRESS: { bg: 'bg-yellow-100', border: 'border-l-yellow-500', text: 'text-yellow-900' },
  COMPLETED: { bg: 'bg-gray-100', border: 'border-l-gray-400', text: 'text-gray-700' },
  CANCELLED: { bg: 'bg-red-50', border: 'border-l-red-400', text: 'text-red-700 line-through opacity-60' },
  NO_SHOW: { bg: 'bg-orange-100', border: 'border-l-orange-500', text: 'text-orange-900' },
};

function overlaps(a0: number, a1: number, b0: number, b1: number) {
  return a0 < b1 && a1 > b0;
}

export default function DayTimelineView({
  appointments,
  selectedAppointmentId,
  onSelectAppointment,
  onTimeSlotClick,
  viewDate,
  onAppointmentMoveEnd,
  startHour = 7,
  endHour = 21,
}: DayTimelineViewProps) {
  const hours = useMemo(() => {
    const result: number[] = [];
    for (let h = startHour; h <= endHour; h++) result.push(h);
    return result;
  }, [startHour, endHour]);

  const dayStartMinutes = startHour * 60;
  const dayEndMinutes = (endHour + 1) * 60;

  const getAppointmentPosition = useCallback(
    (appointment: Appointment) => {
      const start = new Date(appointment.startTime);
      const effEnd = new Date(effectiveEndMs(appointment.startTime, appointment.endTime));

      const startMinutes = start.getHours() * 60 + start.getMinutes();
      const endMinutes = effEnd.getHours() * 60 + effEnd.getMinutes();

      const clampedStartMinutes = Math.max(startMinutes, dayStartMinutes);
      const clampedEndMinutes = Math.min(endMinutes, dayEndMinutes);

      const top = ((clampedStartMinutes - dayStartMinutes) / 60) * ROW_HEIGHT;
      const naturalH = ((clampedEndMinutes - clampedStartMinutes) / 60) * ROW_HEIGHT;
      /** Minimo = un quarto d’ora in px (mai usare un valore fisso in px che equivalga a 30+ min con ROW_HEIGHT attuale). */
      const height = Math.max(naturalH, QUARTER_HEIGHT);

      return { top, height };
    },
    [dayStartMinutes, dayEndMinutes],
  );

  const visibleAppointments = useMemo(() => {
    return appointments.filter((apt) => {
      const start = new Date(apt.startTime);
      const effEndDate = new Date(effectiveEndMs(apt.startTime, apt.endTime));
      const aptStartHour = start.getHours();
      const aptEndHour = effEndDate.getHours() + (effEndDate.getMinutes() > 0 ? 1 : 0);

      return aptStartHour < endHour + 1 && aptEndHour > startHour;
    });
  }, [appointments, startHour, endHour]);

  const appointmentColumns = useMemo(() => {
    const sorted = [...visibleAppointments].sort(
      (a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
    );

    const columns: { appointment: Appointment; column: number; totalColumns: number }[] = [];
    const activeColumns: { endTime: number; appointments: Appointment[] }[] = [];

    for (const apt of sorted) {
      const startMs = new Date(apt.startTime).getTime();
      const endMs = effectiveEndMs(apt.startTime, apt.endTime);

      let columnIndex = activeColumns.findIndex((col) => col.endTime <= startMs);

      if (columnIndex === -1) {
        columnIndex = activeColumns.length;
        activeColumns.push({ endTime: endMs, appointments: [apt] });
      } else {
        activeColumns[columnIndex] = {
          endTime: endMs,
          appointments: [...activeColumns[columnIndex].appointments, apt],
        };
      }

      columns.push({ appointment: apt, column: columnIndex, totalColumns: 1 });
    }

    for (const item of columns) {
      const aptStart = new Date(item.appointment.startTime).getTime();
      const aptEnd = effectiveEndMs(item.appointment.startTime, item.appointment.endTime);

      const overlapping = columns.filter((other) => {
        const otherStart = new Date(other.appointment.startTime).getTime();
        const otherEnd = effectiveEndMs(other.appointment.startTime, other.appointment.endTime);
        return otherStart < aptEnd && otherEnd > aptStart;
      });

      item.totalColumns = Math.max(...overlapping.map((o) => o.column)) + 1;
    }

    return columns;
  }, [visibleAppointments]);

  const formatTime = (dateString: string) => format(new Date(dateString), 'HH:mm');

  const totalHeight = (endHour - startHour + 1) * ROW_HEIGHT;

  const yOffsetToSnappedMinutes = useCallback(
    (yOffset: number) => {
      const clampedY = Math.max(0, Math.min(yOffset, totalHeight - 1));
      let totalMinutes = dayStartMinutes + (clampedY / ROW_HEIGHT) * 60;
      totalMinutes = Math.round(totalMinutes / 15) * 15;
      return Math.max(dayStartMinutes, Math.min(totalMinutes, endHour * 60 + 45));
    },
    [dayStartMinutes, totalHeight, endHour],
  );

  const minutesToDate = useCallback(
    (minutesFromMidnight: number) => {
      const d = new Date(viewDate);
      d.setHours(0, 0, 0, 0);
      const h = Math.floor(minutesFromMidnight / 60);
      const m = minutesFromMidnight % 60;
      d.setHours(h, m, 0, 0);
      return d;
    },
    [viewDate],
  );

  const findValidSlot = useCallback(
    (
      candidateStart: Date,
      durationMs: number,
      assigneeId: string | undefined,
      excludeId: string,
    ): { start: Date; end: Date } => {
      const busy = appointments
        .filter(
          (a) =>
            a.id !== excludeId &&
            a.status !== 'CANCELLED' &&
            assigneeId &&
            a.userId === assigneeId,
        )
        .map((a) => ({
          s: new Date(a.startTime).getTime(),
          e: effectiveEndMs(a.startTime, a.endTime),
        }))
        .sort((a, b) => a.s - b.s);

      const checkSlotAt = (startMs: number): { start: Date; end: Date } | null => {
        const endMs = startMs + durationMs;
        const minStart = minutesToDate(dayStartMinutes).getTime();
        const maxEnd = minutesToDate(dayEndMinutes).getTime();
        if (startMs < minStart || endMs > maxEnd) return null;
        for (const b of busy) {
          if (overlaps(startMs, endMs, b.s, b.e)) return null;
        }
        return { start: new Date(startMs), end: new Date(endMs) };
      };

      let ms = candidateStart.getTime();
      ms = Math.round(ms / SNAP_MS) * SNAP_MS;

      for (let i = 0; i < 200; i++) {
        const t = checkSlotAt(ms);
        if (t) return t;
        ms += SNAP_MS;
      }

      ms = Math.round(candidateStart.getTime() / SNAP_MS) * SNAP_MS;
      for (let i = 0; i < 200; i++) {
        ms -= SNAP_MS;
        const t = checkSlotAt(ms);
        if (t) return t;
      }

      const minStart = minutesToDate(dayStartMinutes);
      return { start: minStart, end: new Date(minStart.getTime() + durationMs) };
    },
    [appointments, minutesToDate, dayStartMinutes, dayEndMinutes],
  );

  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragDeltaY, setDragDeltaY] = useState(0);
  const [draggingAppointment, setDraggingAppointment] = useState<Appointment | null>(null);
  const dragRef = useRef<{
    pointerId: number;
    startClientY: number;
    origTop: number;
    appointment: Appointment;
    moved: boolean;
    captureEl: HTMLElement;
  } | null>(null);

  const skipClickRef = useRef(false);

  const otherAssigneeBands = useMemo(() => {
    if (!draggingAppointment || !onAppointmentMoveEnd) return [];
    const aid = draggingAppointment.userId;
    return visibleAppointments.filter((a) => a.id !== draggingAppointment.id && a.userId && a.userId !== aid);
  }, [visibleAppointments, draggingAppointment, onAppointmentMoveEnd]);

  const ghostTop = draggingAppointment
    ? getAppointmentPosition(draggingAppointment).top + dragDeltaY
    : 0;
  const ghostHeight = draggingAppointment ? getAppointmentPosition(draggingAppointment).height : 0;

  const highlightedQuarterKeys = useMemo(() => {
    const set = new Set<string>();
    if (!draggingId || draggingId === null) return set;
    const g0 = ghostTop;
    const g1 = ghostTop + ghostHeight;
    for (let hour = startHour; hour <= endHour; hour++) {
      for (const min of [0, 15, 30, 45]) {
        const idx = hour - startHour;
        const top = idx * ROW_HEIGHT + (min / 15) * QUARTER_HEIGHT;
        const seg1 = top + QUARTER_HEIGHT;
        if (g0 < seg1 && g1 > top) {
          set.add(`${hour}-${min}`);
        }
      }
    }
    return set;
  }, [draggingId, ghostTop, ghostHeight, startHour, endHour]);

  useEffect(() => {
    if (!draggingId) return;

    const onMove = (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d || e.pointerId !== d.pointerId) return;
      const dy = e.clientY - d.startClientY;
      if (Math.abs(dy) > DRAG_THRESHOLD_PX) d.moved = true;
      setDragDeltaY(dy);
    };

    const onUp = async (e: PointerEvent) => {
      const d = dragRef.current;
      if (!d || e.pointerId !== d.pointerId) return;
      dragRef.current = null;
      setDraggingId(null);
      setDraggingAppointment(null);
      const delta = e.clientY - d.startClientY;
      const apt = d.appointment;

      try {
        d.captureEl.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }

      if (!d.moved && Math.abs(delta) <= DRAG_THRESHOLD_PX) {
        skipClickRef.current = true;
        onSelectAppointment(apt);
        setDragDeltaY(0);
        return;
      }

      setDragDeltaY(0);

      if (!onAppointmentMoveEnd) return;

      const newTop = d.origTop + delta;
      const snappedMin = yOffsetToSnappedMinutes(newTop);
      const candidateStart = minutesToDate(snappedMin);

      const dur =
        new Date(apt.endTime).getTime() - new Date(apt.startTime).getTime() <= 0
          ? MIN_DURATION_MS
          : new Date(apt.endTime).getTime() - new Date(apt.startTime).getTime();

      const { start, end } = findValidSlot(candidateStart, dur, apt.userId, apt.id);

      const oldStart = new Date(apt.startTime).getTime();
      const oldEnd = new Date(apt.endTime).getTime();
      if (start.getTime() === oldStart && end.getTime() === oldEnd) return;

      await onAppointmentMoveEnd(apt, start, end);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [
    draggingId,
    onSelectAppointment,
    onAppointmentMoveEnd,
    yOffsetToSnappedMinutes,
    minutesToDate,
    findValidSlot,
  ]);

  const handleBlockPointerDown = (e: React.PointerEvent, apt: Appointment) => {
    if (apt.status === 'CANCELLED') return;
    if (!onAppointmentMoveEnd) return;
    if (e.button !== 0) return;
    const { top } = getAppointmentPosition(apt);
    const el = e.currentTarget as HTMLElement;
    dragRef.current = {
      pointerId: e.pointerId,
      startClientY: e.clientY,
      origTop: top,
      appointment: apt,
      moved: false,
      captureEl: el,
    };
    setDraggingId(apt.id);
    setDraggingAppointment(apt);
    setDragDeltaY(0);
    el.setPointerCapture(e.pointerId);
    e.preventDefault();
  };

  const handleBlockClick = (e: React.MouseEvent, apt: Appointment) => {
    e.stopPropagation();
    if (skipClickRef.current) {
      skipClickRef.current = false;
      return;
    }
    if (onAppointmentMoveEnd && apt.status !== 'CANCELLED') {
      return;
    }
    onSelectAppointment(apt);
  };

  return (
    <div className="bg-white rounded-lg border shadow-sm overflow-hidden w-full min-w-0">
      <div className="overflow-y-auto max-h-[calc(100vh-280px)] w-full">
        <div className="relative w-full" style={{ height: totalHeight }}>
          {hours.map((hour, index) => (
            <div
              key={hour}
              className="absolute w-full flex border-b border-gray-200"
              style={{ top: index * ROW_HEIGHT, height: ROW_HEIGHT }}
            >
              <div className="w-[4.5rem] shrink-0 pr-1.5 flex flex-col text-right select-none border-r border-gray-200">
                {[0, 15, 30, 45].map((min) => {
                  const key = `${hour}-${min}`;
                  const hl = highlightedQuarterKeys.has(key);
                  return (
                    <div
                      key={min}
                      className={cn(
                        'flex items-start justify-end leading-none cursor-pointer rounded-sm transition-all duration-150',
                        min === 0 ? 'text-sm font-medium text-gray-600 pt-0.5' : 'text-[11px] text-gray-400 pt-0.5',
                        hl && 'scale-110 z-30 text-blue-700 font-semibold bg-blue-50/80',
                        !hl && 'hover:text-blue-600 hover:bg-blue-50/60',
                      )}
                      style={{ height: QUARTER_HEIGHT, minHeight: QUARTER_HEIGHT }}
                      onClick={() => onTimeSlotClick?.(hour, min)}
                    >
                      {hour.toString().padStart(2, '0')}:{min.toString().padStart(2, '0')}
                    </div>
                  );
                })}
              </div>

              <div className="flex-1 border-l border-gray-200 relative bg-gray-50/30">
                <div className="absolute w-full border-b border-gray-100/80" style={{ top: QUARTER_HEIGHT }} />
                <div className="absolute w-full border-b border-dashed border-gray-200" style={{ top: QUARTER_HEIGHT * 2 }} />
                <div className="absolute w-full border-b border-gray-100/80" style={{ top: QUARTER_HEIGHT * 3 }} />
              </div>
            </div>
          ))}

          {draggingId &&
            otherAssigneeBands.map((apt) => {
              const { top, height } = getAppointmentPosition(apt);
              return (
                <div
                  key={`gray-${apt.id}`}
                  className="absolute left-[4.5rem] right-0 pointer-events-none bg-gray-400/25 border-y border-gray-300/40 z-[5]"
                  style={{ top: top + 1, height: Math.max(height - 2, 8) }}
                />
              );
            })}

          <div
            className={cn(
              'absolute left-[4.5rem] right-0 top-0',
              onTimeSlotClick && !draggingId && 'cursor-pointer',
            )}
            style={{ height: totalHeight }}
            onClick={(e) => {
              if (!onTimeSlotClick || draggingId) return;
              const rect = e.currentTarget.getBoundingClientRect();
              const yOffset = Math.max(0, Math.min(e.clientY - rect.top, rect.height - 1));
              const totalMinutes = startHour * 60 + (yOffset / ROW_HEIGHT) * 60;
              let snapped = Math.round(totalMinutes / 15) * 15;
              const minM = startHour * 60;
              const maxM = endHour * 60 + 45;
              snapped = Math.max(minM, Math.min(snapped, maxM));
              const hour = Math.floor(snapped / 60);
              const minute = snapped % 60;
              onTimeSlotClick(hour, minute);
            }}
          >
            {appointmentColumns.map(({ appointment, column, totalColumns }) => {
              const { top, height } = getAppointmentPosition(appointment);
              const colors = statusColors[appointment.status] || statusColors.SCHEDULED;
              const isSelected = appointment.id === selectedAppointmentId;
              const isDragging = draggingId === appointment.id;

              const columnWidth = 100 / totalColumns;
              const leftPercent = column * columnWidth;

              const style: CSSProperties = {
                top: top + 1,
                height: height - 2,
                left: `calc(${leftPercent}% + 2px)`,
                width: `calc(${columnWidth}% - 4px)`,
                transform: isDragging ? `translateY(${dragDeltaY}px)` : undefined,
                zIndex: isDragging ? 40 : undefined,
                touchAction: onAppointmentMoveEnd ? 'none' : undefined,
              };

              return (
                <div
                  key={appointment.id}
                  className={cn(
                    'absolute rounded-md border-l-4 px-2 py-1 transition-shadow overflow-hidden',
                    colors.bg,
                    colors.border,
                    onAppointmentMoveEnd && appointment.status !== 'CANCELLED'
                      ? 'cursor-grab active:cursor-grabbing'
                      : 'cursor-pointer',
                    isSelected && !isDragging ? 'ring-2 ring-blue-500 shadow-lg z-20' : '',
                    !isSelected && !isDragging ? 'hover:shadow-md hover:z-10' : '',
                    isDragging ? 'shadow-xl ring-2 ring-blue-400 opacity-95' : '',
                  )}
                  style={style}
                  onPointerDown={(e) => handleBlockPointerDown(e, appointment)}
                  onClick={(e) => handleBlockClick(e, appointment)}
                >
                  <div className="flex items-center gap-2 h-full flex-wrap pointer-events-none">
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                      {formatTime(appointment.startTime)}-{formatTime(appointment.endTime)}
                    </span>
                    <span className={cn('font-semibold text-sm truncate', colors.text)}>{appointment.title}</span>
                    {appointment.user && (
                      <span className="text-xs text-gray-600 truncate">
                        • {appointment.user.firstName} {appointment.user.lastName}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
